import React, { useRef, useState, useEffect } from 'react';
import { Upload, Button, Modal, Spin } from 'antd';
import { PlusOutlined, LeftOutlined, RightOutlined, DeleteOutlined, CameraOutlined } from '@ant-design/icons';
import { uploadFile } from '../../utils/uploadFile';

const MAX_IMAGES = 5;

export default function ProductImagesFormField({
  value,
  onChange,
  addButtonText = 'Add image',
  hintText,
  hideHint = false,
  disabled = false,
  capture,
  showCameraButton = false,
  cameraButtonText = 'Camera',
  cameraCapture = 'environment',
  className,
  error,
}) {
  const initialImages = Array.isArray(value) ? value : (value ? [value] : []);
  const [images, setImages] = useState(initialImages);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const defaultHint = `Select one or multiple images (up to ${MAX_IMAGES}). Click an image to view.`;
  const canAddMore = !disabled && !uploading && images.length < MAX_IMAGES;
  const cameraInputRef = useRef(null);
  const storageInputRef = useRef(null);

  useEffect(() => {
    setImages(Array.isArray(value) ? value : (value ? [value] : []));
  }, [value]);

  const addFileViaUpload = async (file) => {
    if (disabled) return false;
    setUploading(true);
    try {
      const { url } = await uploadFile(file);
      if (!url) return false;
      setImages((prev) => {
        if (prev.length >= MAX_IMAGES) return prev;
        const next = [...prev, url].slice(0, MAX_IMAGES);
        if (typeof onChange === 'function') onChange(next);
        return next;
      });
    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setUploading(false);
    }
    return false;
  };

  const onCameraPicked = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    addFileViaUpload(file);
    e.target.value = '';
  };

  const onStoragePicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    for (const file of files) await addFileViaUpload(file);
    e.target.value = '';
  };

  const openPicker = (ref) => {
    ref.current?.click?.();
  };

  const onAddCardClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canAddMore) return;
    if (showCameraButton) setSourcePickerOpen(true);
    else openPicker(storageInputRef);
  };

  const onAddCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onAddCardClick(e);
    }
  };

  const chooseCamera = () => {
    setSourcePickerOpen(false);
    requestAnimationFrame(() => openPicker(cameraInputRef));
  };

  const chooseStorage = () => {
    setSourcePickerOpen(false);
    requestAnimationFrame(() => openPicker(storageInputRef));
  };

  const fileList = images.map((url, index) => ({
    uid: String(index),
    name: `image-${index + 1}.png`,
    status: 'done',
    url,
  }));

  return (
    <div className={`${className || ''} order-edit-upload-area`}>
      <div className="order-edit-upload-gallery">
        <Upload
          className={className}
          accept="image/*"
          capture={capture}
          multiple
          maxCount={MAX_IMAGES}
          listType="picture-card"
          fileList={fileList}
          disabled={disabled}
          openFileDialogOnClick={false}
          showUploadList={{
            showRemoveIcon: false,
          }}
          itemRender={(originNode, file, fileList, { remove, preview }) => (
            <div
              className="order-edit-upload-item-wrap"
              onClick={(e) => {
                if (!e.target.closest('.order-edit-upload-remove-btn')) preview();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!e.target.closest('.order-edit-upload-remove-btn')) preview();
                }
              }}
              role="button"
              tabIndex={0}
            >
              {originNode}
              {!disabled && (
                <button
                  type="button"
                  className="order-edit-upload-remove-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove();
                  }}
                  aria-label="Remove image"
                >
                  <DeleteOutlined />
                </button>
              )}
            </div>
          )}
          beforeUpload={addFileViaUpload}
          onRemove={(file) => {
            if (disabled) return false;
            const index = images.findIndex((_, i) => String(i) === file.uid);
            if (index === -1) return;
            setImages((prev) => {
              const next = prev.filter((_, i) => i !== index);
              if (typeof onChange === 'function') onChange(next);
              return next;
            });
          }}
          onPreview={(file) => {
            const index = images.findIndex((_, i) => String(i) === file.uid);
            setPreviewIndex(Math.max(0, index));
            setPreviewOpen(true);
          }}
        >
          {canAddMore && (
            <div
              className="order-edit-add-image-inner order-edit-add-image-trigger"
              role="button"
              tabIndex={0}
              onClick={onAddCardClick}
              onKeyDown={onAddCardKeyDown}
            >
              {uploading ? <Spin size="small" /> : <PlusOutlined className="order-edit-add-image-icon" />}
              <span className="order-edit-add-image-text">{uploading ? 'Uploading...' : addButtonText}</span>
            </div>
          )}
        </Upload>
      </div>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture={cameraCapture}
        onChange={onCameraPicked}
        disabled={!canAddMore}
        hidden
        style={{ display: 'none' }}
      />
      <input
        ref={storageInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onStoragePicked}
        disabled={!canAddMore}
        hidden
        style={{ display: 'none' }}
      />
      {!hideHint && (
        <p className="order-edit-upload-hint">{hintText != null ? hintText : defaultHint}</p>
      )}
      {error && (
        <div className="product-images-field-error" style={{ marginTop: 4, color: '#ff4d4f', fontSize: 12 }}>
          {error}
        </div>
      )}
      <Modal
        title={addButtonText}
        open={sourcePickerOpen}
        onCancel={() => setSourcePickerOpen(false)}
        footer={null}
        centered
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Button type="primary" icon={<CameraOutlined />} onClick={chooseCamera}>
            {cameraButtonText}
          </Button>
          <Button onClick={chooseStorage}>स्टोरेज से चुनें</Button>
        </div>
      </Modal>
      <Modal
        title={`Image ${previewIndex + 1} of ${images.length}`}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width="auto"
        centered
        className="order-edit-preview-modal"
        styles={{
          body: { padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 },
        }}
      >
        {images.length > 0 && (
          <>
            {previewIndex > 0 && (
              <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={() => setPreviewIndex((i) => i - 1)}
                className="order-edit-preview-nav"
              />
            )}
            <img
              src={images[previewIndex]}
              alt={`Preview ${previewIndex + 1}`}
              className="order-edit-preview-img"
            />
            {previewIndex < images.length - 1 && (
              <Button
                type="text"
                icon={<RightOutlined />}
                onClick={() => setPreviewIndex((i) => i + 1)}
                className="order-edit-preview-nav"
              />
            )}
          </>
        )}
      </Modal>
    </div>
  );
}
